/**
 * News tab — persistent RSS sources, ingestion runs, article visibility,
 * and entity-match diagnostics for the homepage/manager signal pipeline.
 */

import { useState, type FormEvent } from 'react';
import {
  ExternalLink,
  Eye,
  EyeOff,
  GitCompare,
  RefreshCw,
  Rss,
  ShieldCheck,
  Tags,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import {
  type AdminNewsItem,
  type AdminNewsTag,
  type AdminNewsSource,
  type AdminNewsStoryCopy,
  type AdminNewsStoryGroup,
  type LeagueName,
  useAdminRankedNewsFeed,
  useAdminNewsStoryGroups,
  useAdminNewsSources,
  useCreateNewsSource,
  useRebuildNewsStoryGroups,
  useRefreshNewsSource,
  useUpdateNewsArticle,
  useUpdateNewsSource,
} from './api';
import { DASH, DataTable, DataTableShell, FilterSelect, Pagination, formatTimestamp } from './shared';

export function NewsTab({ league }: { league: LeagueName }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [language, setLanguage] = useState('en');
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('any');
  const [classification, setClassification] = useState('any');
  const [sourceId, setSourceId] = useState('any');
  const [sampleAgentId, setSampleAgentId] = useState('');
  const [countryIso, setCountryIso] = useState('');
  const limit = 50;
  const normalizedCountryIso = league === 'WC' ? countryIso.trim().toUpperCase() : '';

  const sources = useAdminNewsSources(league);
  const storyGroupFilters = {
    q: q.trim() || undefined,
    status,
    classification,
    source_id: sourceId,
    league,
    country_iso: normalizedCountryIso || undefined,
    limit,
    offset,
  };
  const storyGroups = useAdminNewsStoryGroups(storyGroupFilters);
  const rankingPreview = useAdminRankedNewsFeed(sampleAgentId, 12, {
    league,
    countryIso: normalizedCountryIso || undefined,
  });
  const createSource = useCreateNewsSource();
  const updateSource = useUpdateNewsSource();
  const refreshSource = useRefreshNewsSource();
  const rebuildStoryGroups = useRebuildNewsStoryGroups();
  const updateArticle = useUpdateNewsArticle();

  const sourceRows = sources.data?.data ?? [];
  const storyRows = storyGroups.data?.data ?? [];
  const storyMeta = storyGroups.data?.meta;
  const rankedRows = rankingPreview.data?.data ?? [];

  async function onCreateSource(event: FormEvent) {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName || !trimmedUrl) return;
    // The source is implicitly scoped to the active dataset tab. Admins
    // pick the right RSS feed for the league they're configuring; we
    // never expose a cross-league or "global" concept in the UI.
    await createSource.mutateAsync({
      name: trimmedName,
      url: trimmedUrl,
      enabled: true,
      poll_interval_s: 900,
      league_name: league,
      language: normalizeSourceLanguage(language),
    });
    setName('');
    setUrl('');
    setLanguage('en');
  }

  return (
    <div className="space-y-4">
      <section className="rounded-md border bg-card px-4 py-3">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">RSS sources</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Admin-configured feeds only. Each refresh processes the latest 25 feed items and
              persists dedupe, entity matches, and run logs.
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Source trust: Verified means a curated source with a ranking boost; Reporting means
              acceptable reporting or aggregation with no verified-source boost.
            </div>
          </div>
          <Badge variant="outline">{sourceRows.length} sources</Badge>
        </div>

        <form onSubmit={onCreateSource} className="mb-4 grid gap-2 md:grid-cols-[220px_1fr_96px_auto]">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Source name"
            aria-label="Source name"
          />
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/feed.xml"
            aria-label="RSS URL"
          />
          <Input
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            placeholder="en"
            aria-label="Source language"
            maxLength={16}
          />
          <Button type="submit" disabled={createSource.isPending || !name.trim() || !url.trim()}>
            <Rss className="mr-2 h-4 w-4" />
            Add source
          </Button>
        </form>

        <DataTableShell
          loading={sources.isLoading}
          error={sources.error}
          empty={sourceRows.length === 0}
          emptyLabel="No RSS sources configured"
        >
          <DataTable<AdminNewsSource>
            rows={sourceRows}
            rowKey={(r) => r.id}
            columns={[
              {
                key: 'name',
                label: 'Source',
                render: (r) => (
                  <div className="max-w-md">
                    <div className="font-medium text-foreground">{r.name}</div>
                    <div className="mt-1 truncate text-xs text-muted-foreground">{r.url}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      <CredibilityBadge tier={r.credibility_tier} />
                      <Badge variant="outline">{r.league_name ?? 'Global'}</Badge>
                      <Badge variant="outline">{r.language.toUpperCase()}</Badge>
                      {typeof r.article_count === 'number' ? (
                        <Badge variant="secondary">{r.article_count} articles</Badge>
                      ) : null}
                    </div>
                    {r.last_error ? (
                      <div className="mt-1 text-xs text-destructive">{r.last_error}</div>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'status',
                label: 'Status',
                render: (r) => (
                  <div className="space-y-1">
                    <Badge variant={r.enabled ? 'default' : 'secondary'}>
                      {r.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <div className="text-xs text-muted-foreground">
                      Every {Math.round(r.poll_interval_s / 60)}m
                    </div>
                  </div>
                ),
              },
              {
                key: 'lastRun',
                label: 'Last run',
                render: (r) => <LastRun source={r} />,
              },
              {
                key: 'actions',
                label: '',
                align: 'right',
                render: (r) => (
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={updateSource.isPending}
                      onClick={() =>
                        updateSource.mutate({
                          sourceId: r.id,
                          patch: { enabled: !r.enabled },
                        })
                      }
                    >
                      {r.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={refreshSource.isPending}
                      onClick={() => refreshSource.mutate(r.id)}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Refresh
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        </DataTableShell>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card px-4 py-3">
          <div>
            <div className="text-sm font-medium">Story groups</div>
            <div className="mt-1 text-xs text-muted-foreground">
              One row per underlying story. Copies are source articles; visible copies can appear in app,
              hidden copies stay auditable inside the group.
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => storyGroups.refetch()}
              disabled={storyGroups.isFetching}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={rebuildStoryGroups.isPending}
              onClick={() => rebuildStoryGroups.mutate(league)}
              title="Reassign existing articles into the latest story grouping rules."
            >
              <GitCompare className="mr-2 h-4 w-4" />
              Rebuild groups
            </Button>
          </div>
        </div>

        <div className="rounded-md border bg-card px-4 py-3">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto]">
            <Input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              placeholder="Search story title"
              aria-label="Search story groups"
            />
            <FilterSelect
              label="Source"
              value={sourceId}
              onChange={(v) => {
                setSourceId(v);
                setOffset(0);
              }}
              options={[
                { value: 'any', label: 'All sources' },
                ...sourceRows.map((s) => ({ value: s.id, label: s.name })),
              ]}
            />
            <FilterSelect
              label="Status"
              value={status}
              onChange={(v) => {
                setStatus(v);
                setOffset(0);
              }}
              options={[
                { value: 'any', label: 'All status' },
                { value: 'visible', label: 'Visible' },
                { value: 'hidden', label: 'Hidden' },
              ]}
            />
            <FilterSelect
              label="Topic"
              value={classification}
              onChange={(v) => {
                setClassification(v);
                setOffset(0);
              }}
              options={[
                { value: 'any', label: 'All topics' },
                { value: 'injury', label: 'Injury' },
                { value: 'return', label: 'Return' },
                { value: 'transfer', label: 'Transfer' },
                { value: 'analysis', label: 'Analysis' },
                { value: 'general', label: 'General' },
              ]}
            />
            <Input
              value={countryIso}
              onChange={(e) => {
                setCountryIso(e.target.value.toUpperCase());
                setOffset(0);
              }}
              placeholder="WC country ISO"
              aria-label="WC country ISO"
              disabled={league !== 'WC'}
              maxLength={10}
              className="md:w-[150px]"
            />
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Topic and country filters decide which story groups appear here. The ranking preview below
            still shows the user-facing representative story per group.
          </div>
        </div>

        <RankingPreview
          league={league}
          agentId={sampleAgentId}
          onAgentIdChange={setSampleAgentId}
          countryIso={normalizedCountryIso}
          rows={rankedRows}
          loading={rankingPreview.isFetching}
          error={rankingPreview.error}
        />

        <DataTableShell
          loading={storyGroups.isLoading}
          error={storyGroups.error}
          empty={storyRows.length === 0}
          emptyLabel="No story groups returned"
        >
          <StoryGroupList
            groups={storyRows}
            onToggleCopy={(copy) =>
              updateArticle.mutate({
                articleId: copy.id,
                status: copy.status === 'visible' ? 'hidden' : 'visible',
              })
            }
            updating={updateArticle.isPending}
          />
          {storyMeta ? (
            <Pagination
              total={storyMeta.total}
              limit={storyMeta.limit}
              offset={storyMeta.offset}
              onChange={setOffset}
            />
          ) : null}
        </DataTableShell>
      </section>
    </div>
  );
}

function StoryGroupList({
  groups,
  onToggleCopy,
  updating,
}: {
  groups: AdminNewsStoryGroup[];
  onToggleCopy: (copy: AdminNewsStoryCopy) => void;
  updating: boolean;
}) {
  return (
    <div className="divide-y">
      {groups.map((group) => (
        <StoryGroupRow
          key={group.id}
          group={group}
          onToggleCopy={onToggleCopy}
          updating={updating}
        />
      ))}
    </div>
  );
}

function StoryGroupRow({
  group,
  onToggleCopy,
  updating,
}: {
  group: AdminNewsStoryGroup;
  onToggleCopy: (copy: AdminNewsStoryCopy) => void;
  updating: boolean;
}) {
  const summary = storyGroupBrief(group);
  const copyClusters = clusterStoryCopiesByOriginalUrl(group.copies);
  const originalLinkCount = copyClusters.length;
  const foldedIngestionCount = Math.max(0, group.copy_count - originalLinkCount);
  const hasMultipleCopies = group.copy_count > 1;
  const singleCopy = hasMultipleCopies ? null : group.copies[0] ?? null;
  return (
    <article className="px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant={group.status === 'visible' ? 'default' : 'secondary'}>
              {group.status === 'visible' ? 'Visible' : 'Hidden'}
            </Badge>
            <Badge variant="outline">{topicLabel(group.topic)}</Badge>
            {hasMultipleCopies ? (
              <>
                <Badge variant="secondary">{articleLinkCountLabel(originalLinkCount)}</Badge>
                {foldedIngestionCount > 0 ? (
                  <Badge variant="outline">{foldedIngestionCount} folded ingestion</Badge>
                ) : null}
                <Badge variant="outline">{group.visible_count} visible</Badge>
                <Badge variant={group.hidden_count > 0 ? 'secondary' : 'outline'}>
                  {group.hidden_count} hidden
                </Badge>
              </>
            ) : null}
          </div>
          <div>
            <h3 className="text-sm font-semibold leading-5 text-foreground">{group.title}</h3>
            {summary ? (
              <p className="mt-1 max-w-4xl text-xs leading-5 text-muted-foreground">{summary}</p>
            ) : null}
          </div>
          {singleCopy ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{singleCopy.source_name}</span>
              <CredibilityBadge tier={singleCopy.credibility_tier} />
            </div>
          ) : null}
          <StoryTagList tags={[...(group.country_tags ?? []), ...(group.entity_tags ?? [])]} />
        </div>
        <div className="space-y-2 text-right text-xs text-muted-foreground">
          <div className="font-medium text-foreground">{formatTimestamp(group.published_at)}</div>
          <div className="mt-1">Updated {formatTimestamp(group.updated_at)}</div>
          {singleCopy ? (
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <CopyOpenLink copy={singleCopy} />
              <CopyVisibilityButton
                copy={singleCopy}
                updating={updating}
                onToggle={() => onToggleCopy(singleCopy)}
              />
            </div>
          ) : null}
        </div>
      </div>

      {hasMultipleCopies ? (
        <>
          <div className="mt-3 overflow-hidden rounded-md border">
            <div className="flex items-center justify-between gap-2 bg-muted/30 px-3 py-2 text-xs font-medium text-muted-foreground">
              <span>Article links</span>
              <span>{foldedIngestionCount > 0 ? 'Same-link feed duplicates folded' : 'Individual source articles'}</span>
            </div>
            <div className="divide-y">
              {copyClusters.map(({ primary, foldedCopies }) => (
                <StoryCopyRow
                  key={primary.id}
                  copy={primary}
                  foldedCopies={foldedCopies}
                  onToggle={() => onToggleCopy(primary)}
                  updating={updating}
                />
              ))}
            </div>
          </div>

          <StoryDebugDetails group={group} />
        </>
      ) : null}
    </article>
  );
}

function CopyOpenLink({ copy }: { copy: AdminNewsStoryCopy }) {
  if (!copy.url) return null;
  return (
    <a
      href={copy.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex h-9 items-center gap-1 rounded-md border px-3 text-xs font-medium text-primary hover:bg-muted/40 hover:underline"
    >
      Open original
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function CopyVisibilityButton({
  copy,
  onToggle,
  updating,
}: {
  copy: AdminNewsStoryCopy;
  onToggle: () => void;
  updating: boolean;
}) {
  return (
    <Button type="button" size="sm" variant="outline" disabled={updating} onClick={onToggle}>
      {copy.status === 'visible' ? (
        <EyeOff className="mr-2 h-4 w-4" />
      ) : (
        <Eye className="mr-2 h-4 w-4" />
      )}
      {copy.status === 'visible' ? 'Hide' : 'Show'}
    </Button>
  );
}

function StoryCopyRow({
  copy,
  foldedCopies = [],
  onToggle,
  updating,
}: {
  copy: AdminNewsStoryCopy;
  foldedCopies?: AdminNewsStoryCopy[];
  onToggle: () => void;
  updating: boolean;
}) {
  return (
    <div
      className={[
        'grid gap-3 px-3 py-3 text-sm md:grid-cols-[minmax(0,1fr)_170px_110px_auto]',
        copy.status === 'hidden' ? 'bg-muted/20 text-muted-foreground' : 'bg-background',
      ].join(' ')}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{copy.source_name}</span>
          <CredibilityBadge tier={copy.credibility_tier} />
          <Badge variant={copy.status === 'visible' ? 'default' : 'secondary'}>
            {copy.status === 'visible' ? 'Visible' : 'Hidden'}
          </Badge>
        </div>
        <div className="mt-1 font-medium leading-5 text-foreground">{copy.title}</div>
        {foldedCopies.length > 0 ? (
          <div className="mt-1 text-xs text-muted-foreground">
            Also ingested by {foldedCopies.map((folded) => folded.source_name).join(', ')}.
          </div>
        ) : null}
      </div>
      <div className="text-xs text-muted-foreground">
        <div className="font-medium text-foreground">{formatTimestamp(copy.published_at)}</div>
        <div className="mt-1">Ingested {formatTimestamp(copy.created_at)}</div>
      </div>
      <div className="flex flex-wrap items-start gap-1">
        <StoryTagList tags={[...(copy.country_tags ?? []), ...(copy.entity_tags ?? [])]} max={2} compact />
      </div>
      <div className="flex items-start justify-end gap-2">
        <CopyOpenLink copy={copy} />
        <CopyVisibilityButton copy={copy} updating={updating} onToggle={onToggle} />
      </div>
    </div>
  );
}

function StoryDebugDetails({ group }: { group: AdminNewsStoryGroup }) {
  return (
    <details className="mt-3 rounded-md border border-dashed bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
      <summary className="cursor-pointer font-medium text-foreground">Debug details</summary>
      <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        <DebugValue label="story_key" value={group.story_key} />
        <DebugValue label="fact_key" value={group.debug?.fact_key} />
        <DebugValue label="cue_key" value={group.debug?.cue_key} />
        <DebugValue label="title_token_key" value={group.debug?.title_token_key} />
        <DebugValue label="canonical_url_hash" value={group.debug?.canonical_url_hash} />
        <DebugValue label="event_date" value={group.debug?.event_date} />
      </div>
      <div className="mt-3 space-y-1">
        {group.copies.map((copy) => (
          <div key={copy.id} className="rounded bg-background px-2 py-1 font-mono text-[11px] break-all">
            {copy.source_name}: matched by {copy.debug?.match_reason ?? copy.duplicate_match_reason ?? DASH}
            {typeof (copy.debug?.match_confidence ?? copy.duplicate_match_confidence) === 'number'
              ? ` (${Math.round((copy.debug?.match_confidence ?? copy.duplicate_match_confidence ?? 0) * 100)}%)`
              : ''}
            {' | '}dedupe {copy.debug?.dedupe_fingerprint ?? copy.dedupe_fingerprint}
            {copy.debug?.match_key ? ` | match ${copy.debug.match_key}` : ''}
          </div>
        ))}
      </div>
    </details>
  );
}

function DebugValue({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="min-w-0 rounded bg-background px-2 py-1">
      <div className="font-medium text-foreground">{label}</div>
      <div className="mt-1 font-mono text-[11px] break-all">{value ?? DASH}</div>
    </div>
  );
}

function RankingPreview({
  league,
  agentId,
  onAgentIdChange,
  countryIso,
  rows,
  loading,
  error,
}: {
  league: LeagueName;
  agentId: string;
  onAgentIdChange: (v: string) => void;
  countryIso: string;
  rows: AdminNewsItem[];
  loading: boolean;
  error: unknown;
}) {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : null;
  return (
    <section className="rounded-md border bg-card px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Ranking preview</div>
          <div className="mt-1 max-w-3xl text-xs text-muted-foreground">
            The homepage shows visible articles sorted by score. Without an agent id this is global
            freshness + topic priority. With an agent id, squad players, captain, teams, and
            competitions boost relevance.
          </div>
        </div>
        <div className="grid w-full gap-2 md:w-[340px]">
          <Input
            value={agentId}
            onChange={(e) => onAgentIdChange(e.target.value)}
            placeholder="Optional agent id"
            aria-label="Sample agent id"
          />
        </div>
      </div>
      {message ? <div className="mt-3 text-xs text-destructive">{message}</div> : null}
      <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {rows.slice(0, 6).map((item, index) => (
          <div key={item.id} className="rounded-md border bg-background px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1">
                <Badge variant="outline">#{index + 1}</Badge>
                <CredibilityBadge tier={item.credibility} />
                {item.source_count && item.source_count > 1 ? (
                  <Badge variant="secondary">{item.source_count} sources</Badge>
                ) : null}
                {item.fact_key ? <Badge variant="secondary">Story</Badge> : null}
              </div>
              <span className="font-mono text-xs text-muted-foreground">
                {item.relevance ? `${Math.round(item.relevance.score)} pts` : DASH}
              </span>
            </div>
            <div className="mt-2 line-clamp-2 text-sm font-medium">{item.title}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {item.source ?? item.kicker} - {item.relevance?.reason ?? 'global fallback ranking'}
            </div>
            {item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Open original
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
            {feedItemBrief(item) ? (
              <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                {feedItemBrief(item)}
              </div>
            ) : null}
            {item.agent_summary?.why_it_matters ? (
              <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                Why: {item.agent_summary.why_it_matters}
              </div>
            ) : null}
            <FeedTagList item={item} />
            {countryIso.trim() && league === 'WC' ? (
              <Badge variant="secondary" className="mt-2 font-mono text-[10px]">
                country_iso={countryIso.trim().toUpperCase()}
              </Badge>
            ) : null}
          </div>
        ))}
      </div>
      {rows.length === 0 && !loading ? (
        <div className="mt-3 rounded-md border border-dashed bg-muted/20 px-3 py-4 text-sm text-muted-foreground">
          No ranked feed items returned for this preview.
        </div>
      ) : null}
      {loading ? <div className="mt-2 text-xs text-muted-foreground">Refreshing ranking...</div> : null}
    </section>
  );
}

function topicLabel(topic: string): string {
  const labels: Record<string, string> = {
    injury: 'Injury',
    transfer: 'Transfer',
    return: 'Return',
    analysis: 'Analysis',
    general: 'General',
  };
  return labels[topic] ?? topic;
}

function LastRun({ source }: { source: AdminNewsSource }) {
  const run = source.recent_runs[0];
  if (!run) return <span className="text-muted-foreground">{formatTimestamp(source.last_run_at)}</span>;
  return (
    <div className="text-xs">
      <div className="font-medium text-foreground">{formatTimestamp(run.started_at)}</div>
      <div className="mt-1 text-muted-foreground">
        {run.status} - {run.fetched_count} fetched - {run.created_count} new - {run.deduped_count} deduped
      </div>
      {run.error ? <div className="mt-1 text-destructive">{run.error}</div> : null}
    </div>
  );
}

function StoryTagList({
  tags,
  max = 6,
  compact = false,
}: {
  tags: AdminNewsTag[];
  max?: number;
  compact?: boolean;
}) {
  const uniqueTags = uniqueTagsByKey(tags);
  if (uniqueTags.length === 0) return null;
  return (
    <div className={compact ? 'flex flex-wrap gap-1' : 'flex flex-wrap gap-1'}>
      {uniqueTags.slice(0, max).map((tag) => (
        <Badge
          key={tagKey(tag)}
          variant="outline"
          title={[tag.reason, confidenceLabel(tag.confidence)].filter(Boolean).join(' - ')}
          className={compact ? 'text-[10px]' : undefined}
        >
          <Tags className="mr-1 h-3 w-3" />
          {tagDisplayLabel(tag)}
        </Badge>
      ))}
      {uniqueTags.length > max ? <Badge variant="secondary">+{uniqueTags.length - max}</Badge> : null}
    </div>
  );
}

function FeedTagList({ item }: { item: AdminNewsItem }) {
  const tags = feedTags(item);
  if (tags.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {tags.slice(0, 4).map((tag) => (
        <Badge key={tagKey(tag)} variant="outline" title={tag.reason ?? undefined}>
          <Tags className="mr-1 h-3 w-3" />
          {tagPrefix(tag)}
          {tag.label}
        </Badge>
      ))}
      {tags.length > 4 ? <Badge variant="secondary">+{tags.length - 4}</Badge> : null}
    </div>
  );
}

function CredibilityBadge({ tier }: { tier?: string | null }) {
  if (!tier) return null;
  const label = credibilityLabel(tier);
  const tone =
    tier === 'verified'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : tier === 'reporting'
        ? 'border-sky-200 bg-sky-50 text-sky-700'
        : tier === 'gossip'
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : tier === 'social'
            ? 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700'
            : 'border-zinc-200 bg-zinc-50 text-zinc-700';
  return (
    <Badge variant="outline" className={tone} title={credibilityDescription(tier)}>
      <ShieldCheck className="mr-1 h-3 w-3" />
      {label}
    </Badge>
  );
}

function feedItemBrief(item: AdminNewsItem): string | null {
  return item.agent_summary?.dek ?? item.brief ?? item.summary ?? null;
}

function storyGroupBrief(group: AdminNewsStoryGroup): string | null {
  return group.agent_summary?.dek ?? group.brief ?? group.summary ?? null;
}

function clusterStoryCopiesByOriginalUrl(
  copies: AdminNewsStoryCopy[],
): Array<{ primary: AdminNewsStoryCopy; foldedCopies: AdminNewsStoryCopy[] }> {
  const byUrl = new Map<string, AdminNewsStoryCopy[]>();
  for (const copy of copies) {
    const key = storyCopyOriginalUrlKey(copy);
    byUrl.set(key, [...(byUrl.get(key) ?? []), copy]);
  }

  return [...byUrl.values()].map((cluster) => {
    const primary = selectStoryCopyPrimary(cluster);
    return {
      primary,
      foldedCopies: cluster.filter((copy) => copy.id !== primary.id),
    };
  });
}

function selectStoryCopyPrimary(copies: AdminNewsStoryCopy[]): AdminNewsStoryCopy {
  return copies.reduce((best, copy) => (storyCopyDisplayScore(copy) > storyCopyDisplayScore(best) ? copy : best));
}

function storyCopyDisplayScore(copy: AdminNewsStoryCopy): number {
  const occurredAt = Date.parse(copy.published_at ?? copy.created_at);
  const freshness = Number.isFinite(occurredAt) ? occurredAt / 86_400_000_000 : 0;
  return (copy.status === 'visible' ? 10_000 : 0) + credibilityRank(copy.credibility_tier) * 100 + freshness;
}

function storyCopyOriginalUrlKey(copy: AdminNewsStoryCopy): string {
  const raw = copy.canonical_url || copy.url || copy.id;
  try {
    const url = new URL(raw);
    for (const key of [...url.searchParams.keys()]) {
      if (/^(utm_|fbclid|gclid|at_|ns_|xtor)/i.test(key)) url.searchParams.delete(key);
    }
    url.hash = '';
    url.searchParams.sort();
    const hostname = url.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
    const path = url.pathname.replace(/\/+$/, '');
    return `${hostname}${path}${url.search ? `?${url.searchParams.toString()}` : ''}`;
  } catch {
    return raw.trim().toLowerCase();
  }
}

function articleLinkCountLabel(count: number): string {
  return `${count} article ${count === 1 ? 'link' : 'links'}`;
}

function credibilityRank(tier: string | null | undefined): number {
  switch (tier) {
    case 'verified':
      return 5;
    case 'reporting':
      return 4;
    case 'community':
      return 3;
    case 'social':
      return 2;
    case 'gossip':
      return 1;
    default:
      return 0;
  }
}

function uniqueTagsByKey(tags: AdminNewsTag[]): AdminNewsTag[] {
  const seen = new Set<string>();
  return tags.filter((tag) => {
    const key = tagKey(tag);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function feedTags(item: AdminNewsItem): AdminNewsTag[] {
  const tags = [...(item.country_tags ?? []), ...(item.entity_tags ?? [])];
  for (const country of item.countries ?? []) {
    tags.push({
      label: country.name,
      type: 'country',
      country_iso: country.code,
      confidence: country.confidence,
      reason: country.reason,
    });
  }

  const seen = new Set<string>();
  return tags.filter((tag) => {
    const key = tagKey(tag);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function tagKey(tag: AdminNewsTag): string {
  if (tag.country_iso) return `country:${tag.country_iso.toUpperCase()}`;
  return `${tag.entity_type ?? tag.type ?? 'tag'}:${tag.entity_id ?? tag.label}`;
}

function tagPrefix(tag: AdminNewsTag): string {
  const type = tag.entity_type ?? tag.type ?? null;
  if (tag.country_iso) return `${tag.country_iso.toUpperCase()} - `;
  if (type === 'team') return 'Team - ';
  if (type === 'player') return 'Player - ';
  if (type === 'country') return 'Country - ';
  return '';
}

function tagDisplayLabel(tag: AdminNewsTag): string {
  return `${tagPrefix(tag)}${tag.label}`;
}

function confidenceLabel(value: number | null | undefined): string | null {
  if (typeof value !== 'number') return null;
  return `${Math.round(value * 100)}%`;
}

function credibilityLabel(tier: string): string {
  const labels: Record<string, string> = {
    verified: 'Verified',
    reporting: 'Reporting',
    gossip: 'Gossip',
    community: 'Community',
    social: 'Social',
  };
  return labels[tier] ?? tier;
}

function credibilityDescription(tier: string): string {
  const descriptions: Record<string, string> = {
    verified: 'Curated source with a ranking boost and a user-facing official-style pill.',
    reporting: 'Accepted reporting or aggregation with no verified-source boost.',
    gossip: 'Rumour-heavy source. Kept visible for monitoring, but ranked more cautiously.',
    community: 'Community source. Useful for leads, but ranked below curated reporting.',
    social: 'Social source. Useful for freshness, but ranked below curated reporting.',
  };
  return descriptions[tier] ?? 'Source trust tier.';
}

function normalizeSourceLanguage(value: string): string {
  const normalized = value.trim().toLowerCase();
  return normalized || 'en';
}
